import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  Observable,
  of,
  BehaviorSubject,
  forkJoin,
  Subscription,
  EMPTY,
} from 'rxjs';
import {
  catchError,
  delay,
  expand,
  map,
  reduce,
  retry,
  retryWhen,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import {
  CaasContentResponse,
  CaasFooter,
  CaasLayout,
  CaasNewsCategoryData,
  CaasNewsData,
  CaasPageTypeGroup,
  CaasPlatformData,
  GCA,
  CaasPodcastEpisodeData,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { environment } from '../environments/environment';
import { MetaData, PodcastItems, PodcastPlatforms } from 'hdi-components';
import { Headlines } from '../../../hdi-components/src/shared/enums/shared.enums';
import BadgeType from '../../../hdi-components/src/shared/enums/badge-type.enums';
import {
  News,
  NewsFilter,
} from '../../../hdi-components/src/shared/interfaces/news.interface';
import { GlobalServices } from './global.service';
import { CaasRoutesService } from '../../../hdi-components/src/services/caas-routes.service';
import MenuItem from '../../../hdi-components/src/shared/interfaces/menuItems.interface';
import { MetaDataService } from './meta-data.service';
import {
  ImageMetaData,
  PageGroup,
  SearchCategory,
} from '../../../hdi-components/src/shared/interfaces/meta-data.interface';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class CaasContentService implements OnDestroy {
  private readonly apiKey = environment.production
    ? ''
    : environment.previewToken;
  private readonly caasUrl = environment.caasUrl;
  isPreview = environment.preview ? 'preview' : 'release';

  private imageUrlsSubject = new BehaviorSubject<
    { resolution: string; uid: string; url: string }[]
  >([]);
  imageUrls$ = this.imageUrlsSubject.asObservable();

  podcastItems: BehaviorSubject<PodcastItems[]> = new BehaviorSubject<
    PodcastItems[]
  >([]);
  podcastPlatforms: BehaviorSubject<PodcastPlatforms[]> = new BehaviorSubject<
    PodcastPlatforms[]
  >([]);
  news: BehaviorSubject<News[]> = new BehaviorSubject<News[]>([]);
  newsFilters: BehaviorSubject<NewsFilter[]> = new BehaviorSubject<
    NewsFilter[]
  >([]);
  searchCategories: BehaviorSubject<SearchCategory[]> = new BehaviorSubject<
    SearchCategory[]
  >([]);
  searchCategoriesSubscription$: Subscription = new Subscription();

  // Define a complete GCA seed object with all required properties
  private seed: GCA = {
    _id: '',
    _etag: { $oid: '' },
    streams: [],
    _returned: 0,
    _embedded: { 'rh:doc': [] },
    _links: {
      crud: {
        href: '',
      },
    },
  };

  private cache: {
    [key: string]:
      | Observable<CaasContentResponse>
      | Observable<GCA>
      | Observable<{ title: string; identifier: string }>
      | Observable<{ identifier: string; name: string }[]>
      | Observable<CaasLayout[]>
      | Observable<News[]>
      | Observable<NewsFilter[]>
      | Observable<MetaData>
      | Observable<CaasPlatformData[]>
      | Observable<CaasPodcastEpisodeData[]>
      | Observable<PageGroup[]>;
  } = {};

  private CACHE_EXPIRY_TIME = 3600000; // 1 hour

  constructor(
    private http: HttpClient,
    private globalServices: GlobalServices,
    private caasRoutesService: CaasRoutesService,
    private metaDataService: MetaDataService,
    private router: Router
  ) {}

  /**
   * Helper function to create a complete GCA seed object
   */
  private createSeed(): GCA {
    return {
      _id: '',
      _etag: { $oid: '' },
      streams: [],
      _returned: 0,
      _embedded: { 'rh:doc': [] },
      _links: {
        crud: {
          href: '',
        },
      },
    };
  }

  getContent(identifier: string): Observable<CaasLayout[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');

    if (!this.cache[identifier]) {
      this.cache[identifier] = this.http
        .get<CaasContentResponse>(this.buildUrl(identifier, this.isPreview), {
          headers,
        })
        .pipe(
          retryWhen((errors) =>
            errors.pipe(
              tap(() => console.warn('Retrying request for content...')),
              delay(2000),
              take(3)
            )
          ),
          map((data) => {
            return (data?.page?.children || []).filter(
              (child): child is CaasLayout => 'children' in child
            );
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching content:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getContent'
            );
            return of([]);
          })
        );
    }
    return this.cache[identifier] as Observable<CaasLayout[]>;
  }

  getMetaData(identifier: string): Observable<MetaData> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['metaData-' + identifier]) {
      this.cache['metaData-' + identifier] = this.http
        .get<CaasContentResponse>(this.buildUrl(identifier, this.isPreview), {
          headers,
        })
        .pipe(
          retryWhen((errors) =>
            errors.pipe(
              tap(() => console.warn('Retrying request for content...')),
              delay(2000),
              take(3)
            )
          ),
          switchMap((data) => {
            if (data?.page?.metaFormData) {
              return this.getSearchCategories().pipe(
                map((searchCategories) => {
                  this.searchCategories.next(searchCategories);
                  const metaData = data.page.metaFormData;
                  if (metaData && Object.keys(metaData).length > 0) {
                    return this.metaDataService.translateMetaData(
                      metaData,
                      searchCategories
                    );
                  }
                  return {} as MetaData;
                })
              );
            }
            return of({} as MetaData);
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching meta data:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getMetaData'
            );
            return of({} as MetaData);
          })
        );
    }
    return this.cache['metaData-' + identifier] as Observable<MetaData>;
  }

  getPlatforms(): Observable<CaasPlatformData[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['platforms']) {
      this.cache['platforms'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{entityType:'Podcast'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          // Handle pagination by expanding the observable stream
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              return this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'Podcast'}",
                  nextPage
                ),
                { headers }
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          // Accumulate the paginated results
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Optionally, preserve or update other properties based on your logic
            };
          }, seed),
          retry(3), // Retry the entire pipeline up to 3 times upon failure
          // Transform the accumulated GCA object to the desired CaasPlatformData[] array
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            return Array.isArray(rhDoc)
              ? (rhDoc as unknown as CaasPlatformData[])
              : [];
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching platforms:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPlatforms'
            );
            return of([]);
          })
        );
    }
    return this.cache['platforms'] as Observable<CaasPlatformData[]>;
  }

  getPodcastEpisodes(): Observable<CaasPodcastEpisodeData[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['podcastEpisodes']) {
      this.cache['podcastEpisodes'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{entityType:'PodcastEpisode'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              return this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'PodcastEpisode'}",
                  nextPage
                ),
                { headers }
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            return Array.isArray(rhDoc)
              ? (rhDoc as unknown as CaasPodcastEpisodeData[])
              : [];
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching podcast episodes:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPodcastEpisodes'
            );
            return of([]);
          })
        );
    }
    return this.cache['podcastEpisodes'] as Observable<
      CaasPodcastEpisodeData[]
    >;
  }

  getPodcastData(): Observable<{
    podcasts: PodcastItems[];
    platforms: PodcastPlatforms[];
  }> {
    return forkJoin([this.getPlatforms(), this.getPodcastEpisodes()]).pipe(
      map(([platforms, podcastEpisodes]) => {
        const podcastItems = podcastEpisodes.map((episode) => {
          const podcastSerie = platforms.find(
            (p) =>
              p.identifier === episode.formData.tt_podcast?.value?.identifier
          );

          if (!podcastSerie) {
            console.warn(
              `Podcast Serie not found for episode: ${episode.identifier}`
            );
          }

          const logoWalls = platforms
            .filter(
              (platform) => platform?.formData?.tt_platforms?.value?.length > 0
            )
            .map((podcastSerie) => {
              return {
                serieId: podcastSerie.identifier,
                platforms: podcastSerie?.formData?.tt_platforms?.value?.map(
                  (platform) => {
                    return {
                      alt:
                        platform.formData?.lt_link_platform?.value?.label ?? '',
                      label:
                        platform.formData?.lt_link_platform?.value?.label ?? '',
                      url: platform.formData?.lt_link_external?.value ?? '',
                      serieId: podcastSerie.identifier,
                    };
                  }
                ),
              };
            });

          this.podcastPlatforms.next(logoWalls);
          return {
            id: episode.formData.tt_anchor_name?.value || '',
            subline: episode.formData.tt_preheader?.value || '',
            headline: episode.formData.tt_title?.value || '',
            content: episode.formData.tt_bodytext?.value || '',
            image: episode.formData.tt_image?.value?.url || '',
            imageIdentifier: episode.formData.tt_image?.value?.identifier || '',
            imageUID: episode.formData.tt_image?.value?.uid || '',
            imageTitle: episode.formData.tt_image_title?.value || '',
            key: episode.identifier,
            url: episode.formData?.tt_podigee_configuration?.value || '',
            title: podcastSerie?.formData.tt_title?.value || '',
            timestamp: new Date(episode.formData.tt_date?.value).getTime(),
            podcastSerieId: episode.formData.tt_podcast?.value?.identifier,
          };
        });
        this.podcastItems.next(podcastItems);

        return {
          podcasts: podcastItems,
          platforms: this.podcastPlatforms.getValue(),
        };
      }),
      catchError((error) => {
        console.error('Error fetching podcast data:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getPodcastData'
        );
        return of({ podcasts: [], platforms: [] });
      })
    );
  }

  getTags(): Observable<
    {
      identifier: string;
      name: string;
    }[]
  > {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['tags']) {
      this.cache['tags'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'Tag'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              return this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'Tag'}",
                  nextPage
                ),
                { headers }
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          map((data) => data._embedded?.['rh:doc'] || []),
          map((data) => {
            return data.map((item) => ({
              identifier: item?.identifier ?? '',
              name: item?.['formData']?.['tt_title']?.value ?? '',
            }));
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching tags:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getTags'
            );
            return of([]);
          })
        );
    }
    return this.cache['tags'] as Observable<
      {
        identifier: string;
        name: string;
      }[]
    >;
  }

  getPageGroups(): Observable<PageGroup[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['pageGroups']) {
      this.cache['pageGroups'] = this.http
        .get<GCA>(
          this.buildUrl('', this.isPreview, "{'entityType':'page_group'}"),
          { headers }
        )
        .pipe(
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn('Expected an array of page groups, but got:', rhDoc);
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            return data.map((item) => ({
              category: item?.['displayName'] ?? 'Unknown category',
              identifier: item?.['identifier'] ?? 'Unknown identifier',
            }));
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching page groups:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPageGroups'
            );
            return of([]);
          })
        );
    }
    return this.cache['pageGroups'] as Observable<PageGroup[]>;
  }

  getTypeGroupConnectors(): Observable<SearchCategory[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['typeGroupConnectors']) {
      this.cache['typeGroupConnectors'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'type_group_connector'}"
          ),
          { headers }
        )
        .pipe(
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn(
                'Expected an array of type group connectors, but got:',
                rhDoc
              );
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            return (data as unknown as CaasPageTypeGroup[]).map((item) => ({
              identifier: item?.identifier ?? '',
              name: item?.displayName ?? '',
              pageGroupIdentifier:
                item?.formData?.tt_page_group?.value?.target?.identifier ?? '',
            }));
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching type group connectors:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getTypeGroupConnectors'
            );
            return of([]);
          })
        );
    }
    return this.cache['typeGroupConnectors'] as Observable<SearchCategory[]>;
  }

  getSearchCategories(): Observable<SearchCategory[]> {
    return forkJoin([this.getPageGroups(), this.getTypeGroupConnectors()]).pipe(
      map(([pageGroups, typeGroupConnectors]) => {
        return typeGroupConnectors.map((searchCategory) => {
          const pageGroup = pageGroups.find(
            (pg) => pg.identifier === searchCategory.pageGroupIdentifier
          );
          return {
            ...searchCategory,
            category: pageGroup?.category ?? '',
          };
        });
      }),
      tap((data) => {
        this.searchCategories.next(data);
      }),
      catchError((error) => {
        console.error('Error fetching search categories:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getSearchCategories'
        );
        return of([]);
      })
    );
  }

  getNewsCategories(): Observable<NewsFilter[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['newsCategories']) {
      this.cache['newsCategories'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'NewsCategory'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              return this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'NewsCategory'}",
                  nextPage
                ),
                { headers }
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          takeWhile((data) => data !== null),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];
            // Update _returned with the total number of accumulated items
            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
            };
          }, seed), // Initialize with a complete GCA seed
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn(
                'Expected an array of news categories, but got:',
                rhDoc
              );
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const newsCategories = data.map((item) => {
              const newsCategoryItem = item as unknown as CaasNewsCategoryData;
              return {
                filter:
                  newsCategoryItem.formData.tt_newsfilter?.value?.label ?? '',
                category: newsCategoryItem.formData.tt_title?.value ?? '',
                identifier:
                  newsCategoryItem.formData.tt_newsfilter?.value?.identifier ??
                  '',
              };
            });

            return newsCategories.reduce(
              (acc, { filter, category, identifier }) => {
                const existingFilter = acc.find(
                  (f: NewsFilter) => f.filter === filter
                );
                if (existingFilter) {
                  (existingFilter as NewsFilter).categories.push(category);
                } else {
                  acc.push({
                    filter: filter,
                    categories: [category],
                    identifier: identifier,
                  });
                }

                this.newsFilters.next(acc);
                return acc;
              },
              [] as NewsFilter[]
            );
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching news categories:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getNewsCategories'
            );
            return of([]);
          })
        );
    }
    return this.cache['newsCategories'] as Observable<NewsFilter[]>;
  }

  getNews(): Observable<News[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['news']) {
      this.cache['news'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'News'}",
            initialPage
          ),
          {
            headers,
          }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              return this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'News'}",
                  nextPage
                ),
                { headers }
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn('Expected an array of news, but got:', rhDoc);
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const newsData = data.filter(
              (item) => item['formData']?.['tt_newscategory']
            ) as unknown as CaasNewsData[];

            const news = newsData.map((newsItem) => {
              return {
                title: newsItem.formData.tt_title?.value ?? 'Untitled',
                titleClasses: '',
                titleSize: Headlines.H5,
                date: this.convertDateFormat(
                  (newsItem.formData.tt_date?.value as string) ?? ''
                ),
                description: newsItem.formData.tt_short?.value ?? '',
                badges: newsItem?.formData?.tt_top?.value
                  ? [
                      {
                        type: BadgeType.CAPSULE,
                        label: 'TOP',
                      },
                      {
                        type: BadgeType.DEFAULT,
                        label:
                          newsItem.formData.tt_newscategory?.value?.label?.split(
                            ' ('
                          )[0] ?? '',
                      },
                    ]
                  : [
                      {
                        type: BadgeType.DEFAULT,
                        label:
                          newsItem.formData.tt_newscategory?.value?.label?.split(
                            ' ('
                          )[0] ?? '',
                      },
                    ],
                imageUID: newsItem.formData.tt_image?.value?.uid ?? '',
                imageIdentifier:
                  newsItem.formData.tt_image?.value?.identifier ?? '',
                imageSrc: newsItem.formData.tt_image?.value?.url ?? '',
                imageAlt: '',
                timestamp:
                  new Date(newsItem.formData.tt_date?.value).getTime() || 0,
                rawLink: newsItem.formData.tt_link,
                identifier: newsItem.identifier ?? '',
              };
            });

            this.news.next(news);
            return news;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching news:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getNews'
            );
            return of([]);
          })
        );
    }
    return this.cache['news'] as Observable<News[]>;
  }

  getImagesMetaData(): Observable<ImageMetaData[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    return this.http
      .get<GCA>(
        this.buildUrl(
          '',
          this.isPreview,
          "{'fsType':'Media','mediaType':'PICTURE', '$or': [{'metaFormData.md_media_alternative.value':{'$exists':true}}, {'metaFormData.media_alternative.value':{'$exists':true}}]}"
        ),
        { headers }
      )
      .pipe(
        map((data) => {
          const rhDoc = data._embedded?.['rh:doc'];
          if (!Array.isArray(rhDoc)) {
            console.warn(
              'Expected an array of image metadata, but got:',
              rhDoc
            );
            return [];
          }
          return rhDoc;
        }),
        map((data) => {
          return data.map((item) => ({
            identifier: item?.identifier ?? '',
            fileName: item?.fileName ?? '',
            metaFormData: {
              mediaAlternative:
                item.metaFormData?.md_media_alternative?.value ??
                item.metaFormData?.media_alternative?.value ??
                '',
            },
          }));
        }),
        catchError((error) => {
          console.error('Error fetching image metadata:', error);
          this.globalServices.handleError(
            error,
            'CaasContentService > getImagesMetaData'
          );
          return of([]);
        })
      );
  }

  convertDateFormat(dateString: string): string {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return '';
    }

    // Extract the day, month, and year
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    // Format the date as "DD.MM.YYYY"
    return `${day}.${month}.${year}`;
  }

  getCustomScripts(): Observable<CaasLayout[]> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'Expected an array for global content, but got:',
            content
          );
          return [];
        }
        return content.filter((item) => item.uid === 'custom_scripts');
      }),
      catchError((error) => {
        console.error('Error fetching custom scripts:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getCustomScripts'
        );
        return of([]);
      })
    );
  }

  getGlobalContent(): Observable<CaasLayout[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['global']) {
      this.cache['global'] = this.http
        .get<GCA>(this.buildUrl('', this.isPreview, "{fsType:'GCAPage'}"), {
          headers,
        })
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'getGlobalContent: Expected _embedded["rh:doc"] to be an array, but got:',
                embeddedDocs
              );
              return [];
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching global content:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getGlobalContent'
            );
            return of([]);
          })
        );
    }
    return this.cache['global'] as Observable<CaasLayout[]>;
  }

  getHeader(): Observable<string[] | null> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'getHeader: Expected content to be an array, but got:',
            content
          );
          return null;
        }
        const headers = content.filter(
          (item) => item?.uid === 'custom_scripts' || item?.uid === 'header'
        );
        if (headers.length === 0) {
          console.warn('getHeader: No header items found.');
          return null;
        }
        // Verwende den zuletzt gefundenen Header
        const latestHeader = headers[headers.length - 1];
        const scripts: string[] = [];

        // Prüfen, ob direkt formData als String oder Objekt vorhanden ist:
        const formData = latestHeader['formData'];
        if (typeof formData === 'string' && formData.trim().length > 0) {
          scripts.push(formData);
        } else if (
          formData &&
          typeof formData === 'object' &&
          'script' in formData &&
          formData.script
        ) {
          scripts.push(formData.script);
        }

        // Falls children vorhanden sind, typisieren wir es als Array von CaasLayout
        if (
          'children' in latestHeader &&
          Array.isArray((latestHeader as { children?: CaasLayout[] }).children)
        ) {
          (latestHeader as { children: CaasLayout[] }).children.forEach(
            (child) => {
              if (
                'children' in child &&
                Array.isArray((child as { children?: CaasLayout[] }).children)
              ) {
                (child as { children: CaasLayout[] }).children.forEach(
                  (section) => {
                    if (
                      section['formData'] &&
                      section['formData'].st_code &&
                      section['formData'].st_code.value
                    ) {
                      scripts.push(section['formData'].st_code.value);
                    }
                  }
                );
              }
            }
          );
        }

        if (scripts.length === 0) {
          console.warn(
            'getHeader: No scripts found in expected format.',
            latestHeader['formData']
          );
          return null;
        }
        return scripts;
      }),
      catchError((error) => {
        console.error('Error fetching Header:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getHeader'
        );
        return of(null);
      })
    );
  }
  getFooter(): Observable<CaasFooter | null> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'getFooter: Expected content to be an array, but got:',
            content
          );
          return null;
        }

        const footers = content.filter((item) => item?.uid === 'footer');
        if (footers.length === 0) {
          console.warn('getFooter: No footer items found.');
          return null;
        }

        const latestFooter = footers.sort((a, b) => {
          const dateA = new Date(a?.['changeInfo']?.date).getTime() || 0;
          const dateB = new Date(b?.['changeInfo']?.date).getTime() || 0;
          return dateB - dateA;
        })[0];

        if (!latestFooter?.['formData']) {
          console.warn('getFooter: Latest footer has no formData.');
          return null;
        }

        return latestFooter['formData'];
      }),
      catchError((error) => {
        console.error('Error fetching footer:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getFooter'
        );
        return of(null);
      })
    );
  }

  getMetaNavigation(): Observable<MenuItem[]> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'MetaNavigation: Expected content to be an array. Received:',
            content
          );
          return [];
        }

        const filteredContent = content.find(
          ({ uid }) => uid === 'metanavigation'
        );
        if (!filteredContent) {
          console.warn(
            'MetaNavigation: No content found with UID "metanavigation".'
          );
          return [];
        }

        const { gc_meta_navigation: metaNavigation } =
          filteredContent['formData'] || {};

        if (!metaNavigation?.value?.length) {
          console.warn('MetaNavigation: "gc_meta_navigation" has no items.');
          return [];
        }

        return metaNavigation.value
          .map((item: CaasLayout) => {
            const title = item?.['formData']?.lt_link_label?.value;
            const identifier =
              item?.['formData']?.lt_link_internal?.value?.identifier || null;
            const url = this.getLinkBasedOnType(item['formData']);

            if (!title || !url) {
              console.warn(
                'MetaNavigation: Skipping item due to missing title or URL. Item:',
                item
              );
              return null;
            }

            return { title, url, identifier };
          })
          .filter((item: MenuItem | null) => item !== null);
      }),
      catchError((error) => {
        console.error(
          'MetaNavigation: Error fetching navigation items.',
          error
        );
        this.globalServices.handleError(
          error,
          'CaasContentService > getMetaNavigation'
        );
        return of([]);
      })
    );
  }

  private getLinkBasedOnType(formData: CaasLayout): string {
    const linkType = formData['lt_link_type']?.value?.identifier;

    if (linkType === 'internal') {
      const internalLink = formData['lt_link_internal']?.value?.identifier;
      if (internalLink) {
        const seoRoute = this.caasRoutesService.getSeoRouteByIdentifier(
          internalLink,
          this.router.config
        );
        if (seoRoute) {
          return seoRoute.path ?? '404'; // Return path if found, else fallback to '404'
        } else {
          console.warn(
            'Internal link identifier does not have a matching SEO route.'
          );
          return '404';
        }
      } else {
        console.warn('Internal link identifier is missing in formData.');
        return '404';
      }
    }

    const externalLink = formData['lt_link_external']?.value;
    if (externalLink) {
      return externalLink;
    }

    console.warn('External link is missing in formData.');
    return '';
  }

  getDataset(): Observable<CaasLayout[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['dataset']) {
      this.cache['dataset'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{fsType:'Dataset'}"),
          {
            headers,
          }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of dataset items, but got:',
                embeddedDocs
              );
              return [];
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching dataset:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getDataset'
            );
            return of([]);
          })
        );
    }
    return this.cache['dataset'] as Observable<CaasLayout[]>;
  }

  getEvents(): Observable<CaasLayout[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['events']) {
      this.cache['events'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{entityType:'Event'}"),
          {
            headers,
          }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of events, but got:',
                embeddedDocs
              );
              return [];
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching events:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getEvents'
            );
            return of([]);
          })
        );
    }
    return this.cache['events'] as Observable<CaasLayout[]>;
  }

  getMedia(): Observable<CaasLayout[]> {
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['media']) {
      this.cache['media'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{fsType:'Media'}"),
          { headers }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of media items, but got:',
                embeddedDocs
              );
              return [];
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching media:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getMedia'
            );
            return of([]);
          })
        );
    }
    return this.cache['media'] as Observable<CaasLayout[]>;
  }

  private buildUrl(
    identifier: string,
    release: string = 'release',
    filter?: string,
    page?: number
  ): string {
    try {
      const urlBase = `${this.caasUrl}.${release}.content`;
      const url = filter
        ? page
          ? `${urlBase}/?filter=${encodeURIComponent(filter)}&page=${page}`
          : `${urlBase}/?filter=${encodeURIComponent(filter)}`
        : `${urlBase}/${identifier}.de_DE`;

      return url;
    } catch (error) {
      console.error('Error building URL:', error);
      return '';
    }
  }

  private getNextPage(data: GCA, currentPage: number): number | null {
    // If _returned is 0, it means there are no more items to fetch
    return data._returned > 0 ? currentPage + 1 : null;
  }

  ngOnDestroy(): void {
    try {
      if (this.searchCategoriesSubscription$) {
        this.searchCategoriesSubscription$.unsubscribe();
      }
    } catch (error) {
      console.error(
        'Error unsubscribing from searchCategoriesSubscription$',
        error
      );
    }
  }
}
